import {
  SortableKeyOf,
  SortDirection,
  sortItems,
} from '../sort-drop-down-select';
import { FilterFunction, TreeNodeDefinition } from './types';

/**
 * Build a map of nodeId -> parentId for quick ancestor lookups.
 */
export const buildParentIdMap = <T>(
  nodes: TreeNodeDefinition<T>[]
): Map<string, string | undefined> => {
  const map = new Map<string, string | undefined>();
  for (const node of nodes) {
    map.set(node.id, node.parentId);
  }
  return map;
};

/**
 * Build a map of parentId -> children for quick child lookups.
 */
export const buildChildrenIndex = <T>(
  nodes: TreeNodeDefinition<T>[]
): Map<string | undefined, TreeNodeDefinition<T>[]> => {
  const index = new Map<string | undefined, TreeNodeDefinition<T>[]>();
  for (const node of nodes) {
    const children = index.get(node.parentId);
    if (children) {
      children.push(node);
    } else {
      index.set(node.parentId, [node]);
    }
  }
  return index;
};

/**
 * Get all ancestor IDs for a node (from node up to root).
 */
export const getAncestorIds = (
  nodeId: string,
  parentIdMap: Map<string, string | undefined>
): string[] => {
  const ancestors: string[] = [];
  let currentId: string | undefined = parentIdMap.get(nodeId);
  while (currentId !== undefined) {
    ancestors.push(currentId);
    currentId = parentIdMap.get(currentId);
  }
  return ancestors;
};

export interface FilterResult {
  visibleIds: Set<string>;
  expandIds: Set<string>;
}

/**
 * Filter nodes by predicate.
 * Returns IDs of nodes that should be visible and IDs of nodes that should be expanded.
 * Includes ancestors of matching nodes so the tree path is visible.
 */
export const filterNodes = <T>(
  nodes: TreeNodeDefinition<T>[],
  predicate: FilterFunction<T>,
  parentIdMap: Map<string, string | undefined>
): FilterResult => {
  const visibleIds = new Set<string>();
  const expandIds = new Set<string>();

  for (const node of nodes) {
    if (predicate(node)) {
      visibleIds.add(node.id);
      const ancestors = getAncestorIds(node.id, parentIdMap);
      for (const ancestorId of ancestors) {
        visibleIds.add(ancestorId);
        expandIds.add(ancestorId);
      }
    }
  }

  return { visibleIds, expandIds };
};

export interface TextFilterResult extends FilterResult {
  highlightText: string;
  matchingIds: Set<string>;
}

/**
 * Filter nodes by text search on label.
 * Returns IDs of nodes that should be visible, expanded, and which ones match the search.
 */
export const filterNodesByText = <T>(
  nodes: TreeNodeDefinition<T>[],
  filter: string,
  parentIdMap: Map<string, string | undefined>
): TextFilterResult => {
  const trimmedFilter = filter.trim();
  const visibleIds = new Set<string>();
  const expandIds = new Set<string>();
  const matchingIds = new Set<string>();

  if (trimmedFilter.length === 0) {
    for (const node of nodes) {
      visibleIds.add(node.id);
    }
    return { visibleIds, expandIds, matchingIds, highlightText: '' };
  }

  const lowerFilter = trimmedFilter.toLowerCase();

  for (const node of nodes) {
    if (node.label.toLowerCase().includes(lowerFilter)) {
      visibleIds.add(node.id);
      matchingIds.add(node.id);
      const ancestors = getAncestorIds(node.id, parentIdMap);
      for (const ancestorId of ancestors) {
        visibleIds.add(ancestorId);
        expandIds.add(ancestorId);
      }
    }
  }

  return { visibleIds, expandIds, matchingIds, highlightText: trimmedFilter };
};

export const isFolder = <T>(
  node: TreeNodeDefinition<T>,
  childrenIndex: Map<string | undefined, TreeNodeDefinition<T>[]>
): boolean => {
  if (node.isLeaf === true) return false;
  if (node.isLeaf === false) return true;
  return childrenIndex.has(node.id);
};

/**
 * Sort nodes. Explicit sortPriority wins first, then folders before leaves,
 * then sorted by sortBy.
 */
export const sortNodes = <T>(
  nodes: TreeNodeDefinition<T>[],
  sortBy: SortableKeyOf<TreeNodeDefinition<T>>,
  sortDirection: SortDirection,
  childrenIndex: Map<string | undefined, TreeNodeDefinition<T>[]>
): TreeNodeDefinition<T>[] => {
  return nodes.toSorted((a, b) => {
    const aPriority = a.sortPriority ?? 0;
    const bPriority = b.sortPriority ?? 0;
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    const aIsFolder = isFolder(a, childrenIndex);
    const bIsFolder = isFolder(b, childrenIndex);

    if (aIsFolder !== bIsFolder) {
      return aIsFolder ? -1 : 1;
    }

    return sortItems(a, b, sortBy, sortDirection);
  });
};
